import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { KwikspaceService } from "src/app/services/kwikspace.service";
import {
  ExportFmlFailure,
  ExportFmlSuccess,
  ExportThreeDFailure,
  ExportThreeDSuccess,
  ExportTwoDFailure,
  ExportTwoDSuccess,
  FloorPlannerActionTypes,
  GetProjectListFailure,
  GetProjectListSuccess,
  GetProjectFailure,
  GetProjectSuccess,
  GetTemplateFailure,
  GetTemplateListFailure,
  GetTemplateListSuccess,
  GetTemplateSuccess,
  ProjectDownloadFailure,
  ProjectDownloadSuccess,
  ProjectExportFailure,
  ProjectExportSuccess,
  CreateDesignFailure,
  CreateDesignSuccess,
  GetProductsListFailure,
  GetProductsListSuccess,
  GetDesignFailure,
  GetDesignSuccess,
  CreateProjectFailure,
  CreateProjectSuccess,
  GetUserKeySuccess,
  GetUserKeyFailure,
  SaveProjectProductsSuccess,
  SaveProjectProductsFailure,
} from "../actions/floorplanner.action";
import { FloorplannerService } from "src/app/services/floorplanner.service";

@Injectable()
export class FloorPlannerEffects {
  public constructor(
    private actions$: Actions,
    private service: KwikspaceService,
    private fpService: FloorplannerService
  ) {}

  @Effect()
  GetProjectList$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_PROJECT_LIST),
    switchMap((action: any) => {
      return this.service.getFloorPlannerProjectList().pipe(
        map((res: any) => new GetProjectListSuccess(res)),
        catchError((error) => of(new GetProjectListFailure(error)))
      );
    })
  );

  @Effect()
  GetTemplateList$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_TEMPLATE_LIST),
    switchMap((action: any) => {
      return this.service.getFloorPlannerTemplateList().pipe(
        map((res: any) => new GetTemplateListSuccess(res.results)),
        catchError((error) => of(new GetTemplateListFailure(error)))
      );
    })
  );

  @Effect()
  GetTemplate$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_TEMPLATE),
    switchMap((action: any) => {
      return this.service.getFloorPlannerTemplate(action.id).pipe(
        map((res: any) => new GetTemplateSuccess(res.results)),
        catchError((error) => of(new GetTemplateFailure(error)))
      );
    })
  );

  @Effect()
  ProjectDownload$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.PROJECT_DOWNLOAD),
    switchMap((action: any) => {
      return this.service.getFloorPlannerProjectDownload(action.id).pipe(
        map((res: any) => new ProjectDownloadSuccess(res.results)),
        catchError((error) => of(new ProjectDownloadFailure(error)))
      );
    })
  );

  @Effect()
  ProjectExport$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.PROJECT_EXPORT),
    switchMap((action: any) => {
      return this.service.getFloorPlannerProjectExport(action.id).pipe(
        map((res: any) => new ProjectExportSuccess(res.results)),
        catchError((error) => of(new ProjectExportFailure(error)))
      );
    })
  );

  @Effect()
  ExportFml$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.EXPORT_FML),
    switchMap((action: any) => {
      return this.service.exportFloorPlannerProjectFml(action.payload).pipe(
        map((res: any) => new ExportFmlSuccess(res.results)),
        catchError((error) => of(new ExportFmlFailure(error)))
      );
    })
  );

  @Effect()
  ExportTwo$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.EXPORT_TWO_D),
    switchMap((action: any) => {
      return this.service.exportFloorPlannerProjectTwoD(action.payload).pipe(
        map((res: any) => new ExportTwoDSuccess(res.results)),
        catchError((error) => of(new ExportTwoDFailure(error)))
      );
    })
  );

  @Effect()
  ExportThree$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.EXPORT_THREE_D),
    switchMap((action: any) => {
      return this.service.exportFloorPlannerProjectThreeD(action.payload).pipe(
        map((res: any) => new ExportThreeDSuccess(res.results)),
        catchError((error) => of(new ExportThreeDFailure(error)))
      );
    })
  );

  @Effect()
  GetProject$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_PROJECT),
    switchMap((action: any) => {
      return this.service.getFloorPlannerProject(action.projectId).pipe(
        map((res: any) => new GetProjectSuccess(res)),
        catchError((error) => of(new GetProjectFailure(error)))
      );
    })
  );

  @Effect()
  CreateProject$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.CREATE_PROJECT),
    switchMap((action: any) => {
      return this.service.createFloorplannerProject(action.payload).pipe(
        map((res: any) => new CreateProjectSuccess(res)),
        catchError((error) => of(new CreateProjectFailure(error)))
      );
    })
  );

  @Effect()
  CreateDesign$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.CREATE_DESIGN),
    switchMap((action: any) => {
      return this.service.createNewDesign(action.payload).pipe(
        map((res: any) => new CreateDesignSuccess(res)),
        catchError((error) => of(new CreateDesignFailure(error)))
      );
    })
  );

  @Effect()
  GetDesign$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_DESIGN),
    switchMap((action: any) => {
      return this.service.getDesign(action.payload).pipe(
        map((res: any) => new GetDesignSuccess(res)),
        catchError((error) => of(new GetDesignFailure(error)))
      );
    })
  );

  @Effect()
  GetProducts$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_PRODUCTS),
    switchMap((action: any) => {
      return this.service.getProjectProducts(action.projectId).pipe(
        map((res: any) => new GetProductsListSuccess(res)),
        catchError((error) => of(new GetProductsListFailure(error)))
      );
    })
  );

  @Effect()
  SaveProjectProducts$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.SAVE_PROJECT_PRODUCTS),
    switchMap((action: any) => {
      return this.service.saveProjectProducts(action.projectId).pipe(
        map((res: any) => new SaveProjectProductsSuccess(res)),
        catchError((error) => of(new SaveProjectProductsFailure(error)))
      );
    })
  );

  @Effect()
  GetUserKey$: Observable<any> = this.actions$.pipe(
    ofType(FloorPlannerActionTypes.GET_USER_KEY),
    switchMap((action: any) => {
      return this.service.getFloorplannerKey(action.userId).pipe(
        map((res: any) => new GetUserKeySuccess(res)),
        catchError((error) => of(new GetUserKeyFailure(error)))
      );
    })
  );
}
